import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TABLES } from "../../../Apis/Tables";
import { getFullName } from "../../../Apis/functions";
import { permissionObj } from "../../../Helpers/permission";
import DropDownMenuItem from "../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../components/DropDown/DropDownMenuProfile";
import {
    DetailsIconsOptions,
    IconMoreVerticalDots,
    IconQr,
    LinkIconsOptions
} from "../../../components/buttons/IconButtons";
import TableFunction from "../../../components/table/TableFunction";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { VIEW_TYPE } from "../../../enums/ViewType";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";
import { RESIDENT_STATUS, RESIDENT_TYPE, downloadResidentQr, getResidentVerificationStatus } from "../Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Registered residents logic of the building detail: paginated search of the
 * residents registered in the building, per card actions menu and the details
 * and registration link modals opened from it.
 *
 * @param {Object} props
 * @param {string|number} props.buildingId - Building the residents belong to.
 * @param {number} props.refresh - Counter bumped by the add modal to reload the list.
 * @param {Function} props.onTotalChange - Reports the total of the list to the badge of the tab.
 * @returns {Object} Localization function, list state and the card handlers.
 */
export const ResidentsRegisteredApp = ({ buildingId, refresh, onTotalChange }) => {
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    // The registration link authorizes a resident to register, it belongs to the residents management
    const canManageResidents = DEV || permission?.includes(permissionObj?.WEB_BUILDING_MANAGE_RESIDENTS);
    const option = TABLES.BUILDING_APARTMENT_RESIDENT;
    const [searchElement, setSearchElement] = useState("");
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [modified, setModified] = useState(false);
    const [detailsModal, setDetailsModal] = useState(false);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    // Flags SearchSimple works with, the search itself runs through getData
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [bodyForExport, setBodyForExport] = useState('');
    // Building the last fetch ran with, the detail is not loaded yet on mount
    const requestedBuildingId = useRef(null);
    // Scrollable container of the grid, panelTables-container sizes it with --top-value
    const elementRef = useRef(null);

    /**
     * Loads the registered residents page the grid renders, the residents of the
     * building are searched through the BUILDING_APARTMENT_RESIDENT model of the
     * simple search by id, which takes the building as the userId of the body.
     *
     * @param {number} page - Page requested by the pagination, zero based.
     * @param {Object} [options] - Extra values sent by TableFunction.
     * @param {number} [options.rowsPerPage] - Page size in use.
     * @param {string} [options.searchElement] - Search value the fetch runs with.
     */
    const getData = (page, options) => {
        // The building is required by the search, on the first render of the
        // detail it is still loading and the fetch is left to the effect below
        if (!buildingId) return;

        requestedBuildingId.current = buildingId;

        const key = options?.searchElement ?? searchElement;

        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: option,
            key,
            userId: buildingId
        });

        setBodyForExport(body);

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success !== true) return;

                // The badge of the tab paints the whole list, a filtered search
                // is not the total of registered residents
                if (!key) onTotalChange?.(data?.totalElements || 0);

                setResults({
                    ...data,
                    // The card paints one name and the apartment it belongs to,
                    // the service returns the name split and the unit by its name
                    content: data?.content?.map((resident) => ({
                        ...resident,
                        fullName: resident?.fullName
                            || getFullName(resident?.name, resident?.lastName, resident?.secondLastName),
                        apartment: resident?.apartment || resident?.unitName,
                        // The chip of the card is the verification status of the resident
                        status: getResidentVerificationStatus(resident?.identityValidationId)
                    })) || []
                });
            }
        );
    };

    // Use TableFunction to handle the grid logic (pagination, actions menu)
    const {
        t,
        pagination,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose
    } = TableFunction({
        data: results,
        labelRowsPerPage: 'residents_per_page',
        hasContainer: true,
        fixedPagination: false,
        view: VIEW_TYPE.GRID.name,
        searchElement,
        getData,
        modified,
        setModified
    });

    // Loads the list once the building detail arrives, the mount fetch of
    // TableFunction is skipped while there is no building to search with
    useEffect(() => {
        if (!buildingId || requestedBuildingId.current === buildingId) return;

        setModified(true);
    }, [buildingId]);

    // Reloads the list after the add modal saves new entries
    useEffect(() => {
        if (!buildingId || !refresh) return;

        setModified(true);
    }, [refresh]);

    /**
     * Distance from the top of the viewport to the grid, panelTables-container
     * takes the rest of the screen with it and leaves the pagination outside
     * of the scroll.
     */
    useLayoutEffect(() => {
        if (!elementRef?.current) return;

        const distanceTop = elementRef.current.getBoundingClientRect().top + 90;
        elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
    }, [results?.content]);

    /**
     * Search of SearchSimple, the residents need the building on the body so the
     * fetch is the same getData of the grid instead of the generic one.
     */
    const handleSearch = () => getData(0);

    /**
     * Reset of SearchSimple, brings the first page back without the search key.
     */
    const handleResetSearch = () => getData(0, { searchElement: "" });

    /**
     * Closes the details modal and clears the resident it was showing.
     */
    const hideDetails = () => {
        setDetailsModal(false);
        setSelectedItem(null);
    };

    /**
     * The owner of the apartment is out of the QR access, it is only offered to
     * the rest of the residents.
     *
     * @param {Object} item - Resident data.
     * @returns {boolean} True when the resident is not the owner.
     */
    const isNotOwner = (item) => item?.residentType !== RESIDENT_TYPE.OWNER;

    /**
     * Registration link, every resident that still has to validate their
     * identity needs it, the owner of the apartment included. It is only
     * offered to whoever manages the residents of the building.
     *
     * @param {Object} item - Resident data.
     * @returns {boolean} True when the registration link is available.
     */
    const canRegisterResident = (item) =>
        canManageResidents
        && getResidentVerificationStatus(item?.identityValidationId) === RESIDENT_STATUS.UNVERIFIED;

    /**
     * QR access, the resident enters the building with it once their identity
     * is already verified.
     *
     * @param {Object} item - Resident data.
     * @returns {boolean} True when the QR access is available.
     */
    const canAccessQr = (item) =>
        isNotOwner(item)
        && getResidentVerificationStatus(item?.identityValidationId) === RESIDENT_STATUS.VERIFIED;

    /**
     * Vertical three dots menu with the actions available for one resident.
     *
     * @param {Object} item - Resident data.
     * @param {number} index - Card position, used for the menu item ids.
     * @returns {JSX.Element}
     */
    const getActionsButtons = (item, index) => {
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item?.id}`}
                icon={<IconMoreVerticalDots />}
            >
                <DropDownMenuItem
                    id={`btn_rrg_details_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={'details'}
                    icon={<DetailsIconsOptions/>}
                    click={() => {
                        setSelectedItem(item);
                        setDetailsModal(true);
                    }}
                />
                {/* A resident that already validated their identity does not
                    need the link, the owner does while they are unverified */}
                {canRegisterResident(item) &&
                    <DropDownMenuItem
                        id={`btn_rrg_registration_link_${item?.id}`}
                        index={index}
                        handleClose={handleClose}
                        name={'registration_link'}
                        icon={<LinkIconsOptions/>}
                        click={() => {
                            // The link is the one of this resident, the modal builds it with their id
                            setSelectedItem(item);
                            setInvitationLinkModal(true);
                        }}
                    />
                }
                {/* The QR is the access of a resident already verified */}
                {canAccessQr(item) &&
                    <DropDownMenuItem
                        id={`btn_rrg_qr_access_${item?.id}`}
                        index={index}
                        handleClose={handleClose}
                        name={'qr_access'}
                        icon={<IconQr/>}
                        click={() => downloadResidentQr(dispatch)}
                    />
                }
            </DropDownMenuProfile>
        );
    };

    return {
        t,
        option,
        searchElement,
        setSearchElement,
        results,
        setResults,
        rowsPerPage,
        search,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        bodyForExport,
        setBodyForExport,
        handleSearch,
        handleResetSearch,
        detailsModal,
        invitationLinkModal,
        setInvitationLinkModal,
        selectedItem,
        setSelectedItem,
        hideDetails,
        getActionsButtons,
        pagination,
        elementRef,
    };
};
